/**
 * M+ rating and rank tiers (PLAN §11 "Rank tiers").
 * Rating = sum over RATED dungeons of the best TIMED run's score, scaled so the tier table
 * means the same thing whether 1 or 8 dungeons exist (see `ratingScale`).
 */
import { DUNGEON, getContentBundle } from '@keystone/content';
import type { DungeonId, Ms } from '@keystone/sim';
import type { RankStanding, RankTier, RankTierDef, RatingRecord, Unlock } from './types';

/**
 * Score tuning, per dungeon: 25 × key level − 5, plus up to 10 for time left.
 * The tier table assumes `expectedDungeons` (8) rated dungeons. Same key everywhere (or the one
 * MVP dungeon, × 8): +3 = Contender, +5 = gems (Contender 3), +6 with 20% of the timer left (or
 * +7) = Champion, +10 timed = Paragon, +14 fast (or +15) = Eternal.
 * Tuned with `pnpm harness -- progress`: each tier's entry key sits at the wall of the previous
 * tier's max drop ilvl (220 ≈ +7–8, 240 ≈ +10–11, 255 ≈ +13–14), so rating and gear climb
 * together and no tier is gated behind gear it cannot yet drop.
 */
export const RATING_TUNING = {
  basePerLevel: 25,
  baseFlat: -5,
  /** Bonus for time remaining: up to this many points at `maxBonusFraction` of the timer left. */
  timeBonusMax: 10,
  maxBonusFraction: 0.4,
  expectedDungeons: 8,
} as const;

/**
 * Dungeons that count toward rating: every bundle M+ dungeon (not the training arena, +field: not
 * open fields / towns, not levelled normal-only dungeons such as the Hollow Barrow).
 */
export const RATED_DUNGEONS: readonly DungeonId[] = getContentBundle()
  .dungeons.filter((d) => !d.field && d.mobLevel === undefined)
  .map((d) => d.id)
  .filter((id) => id !== DUNGEON.trainingGrounds);

export function isRatedDungeon(id: DungeonId, rated: readonly DungeonId[] = RATED_DUNGEONS): boolean {
  return rated.includes(id);
}

/**
 * Multiplier from summed dungeon scores to rating: expectedDungeons / ratedDungeons. With the
 * MVP's single dungeon it is 8, so one dungeon carries the whole ladder; it falls to 1 as the
 * season fills out. Stored per-dungeon scores stay raw, so adding a dungeon re-scales rating
 * without a save migration.
 */
export function ratingScale(ratedCount: number = RATED_DUNGEONS.length): number {
  return RATING_TUNING.expectedDungeons / Math.max(1, Math.min(RATING_TUNING.expectedDungeons, ratedCount));
}

export const RANK_TIERS: readonly RankTierDef[] = [
  { tier: 'novice', name: 'Novice', minRating: 0, subRanks: 3, maxIlvl: 200, maxGemRank: 0, talentTiers: 2, legendaryChance: 0, unlocks: [] },
  {
    tier: 'contender',
    name: 'Contender',
    minRating: 500,
    subRanks: 4,
    maxIlvl: 220,
    maxGemRank: 2,
    talentTiers: 2,
    legendaryChance: 0,
    unlocks: [{ unlock: 'reforge' }, { unlock: 'gems', atSubRank: 3 }],
  },
  {
    tier: 'champion',
    name: 'Champion',
    minRating: 1200,
    subRanks: 4,
    maxIlvl: 240,
    maxGemRank: 3,
    talentTiers: 3,
    legendaryChance: 0.04,
    unlocks: [{ unlock: 'legendaries' }, { unlock: 'capstones' }],
  },
  // +econ-pacing: 1960 = any timed +10 (was 2000: a timed +10 scores 1960–1990, so Paragon really
  // needed a +11 at the ilvl-240 wall, a coin flip that pushed it to runs 23–30).
  { tier: 'paragon', name: 'Paragon', minRating: 1960, subRanks: 4, maxIlvl: 255, maxGemRank: 5, talentTiers: 3, legendaryChance: 0.08, unlocks: [{ unlock: 'highGemRanks' }] },
  { tier: 'eternal', name: 'Eternal', minRating: 2800, subRanks: 5, openSubRankSpan: 100, maxIlvl: 270, maxGemRank: 5, talentTiers: 3, legendaryChance: 0.1, unlocks: [{ unlock: 'maxUpgrade' }] },
];

export function tierDef(tier: RankTier): RankTierDef {
  const d = RANK_TIERS.find((t) => t.tier === tier);
  if (!d) throw new Error(`unknown tier ${tier}`);
  return d;
}

/** Score of one run. Untimed runs score 0 (they do not count toward rating). */
export function scoreRun(run: { level: number; timed: boolean; elapsedMs: Ms; timerMs: Ms }): number {
  if (!run.timed) return 0;
  const base = RATING_TUNING.baseFlat + RATING_TUNING.basePerLevel * run.level;
  const left = run.timerMs > 0 ? Math.max(0, (run.timerMs - run.elapsedMs) / run.timerMs) : 0;
  const bonus = RATING_TUNING.timeBonusMax * Math.min(1, left / RATING_TUNING.maxBonusFraction);
  return Math.round((base + bonus) * 10) / 10;
}

/**
 * Rating: sum of best timed scores over rated dungeons, × ratingScale. Iterates keys in sorted
 * order (deterministic). Pass `rated` to override the dungeon list (tests, future seasons).
 */
export function totalRating(records: Readonly<Record<DungeonId, RatingRecord>>, rated: readonly DungeonId[] = RATED_DUNGEONS): number {
  let sum = 0;
  for (const id of Object.keys(records).sort()) if (isRatedDungeon(id, rated)) sum += records[id]?.best?.score ?? 0;
  return Math.round(sum * ratingScale(rated.length) * 10) / 10;
}

/** Score a dungeon contributes to rating (after scaling): the UI's "per dungeon" column. */
export function dungeonRating(rec: RatingRecord | undefined, rated: readonly DungeonId[] = RATED_DUNGEONS): number {
  if (!rec?.best || !isRatedDungeon(rec.dungeonId, rated)) return 0;
  return Math.round(rec.best.score * ratingScale(rated.length) * 10) / 10;
}

/** Integer rating at which each sub-rank 2..N of a tier starts (ceil, so nextAt is exact). */
export function subRankBoundaries(tier: RankTier): number[] {
  const idx = RANK_TIERS.findIndex((t) => t.tier === tier);
  const def = RANK_TIERS[idx]!;
  const next = RANK_TIERS[idx + 1];
  const span = next ? (next.minRating - def.minRating) / def.subRanks : (def.openSubRankSpan ?? 100);
  return Array.from({ length: def.subRanks - 1 }, (_, k) => Math.ceil(def.minRating + (k + 1) * span));
}

/** Tier, sub-rank and unlocks for a rating. */
export function tierFor(rating: number): RankStanding {
  let idx = 0;
  for (let i = 0; i < RANK_TIERS.length; i++) if (rating >= RANK_TIERS[i]!.minRating) idx = i;
  const def = RANK_TIERS[idx]!;
  const next = RANK_TIERS[idx + 1];
  const bounds = subRankBoundaries(def.tier);
  const subRank = 1 + bounds.filter((b) => rating >= b).length;
  const nextAt = bounds.find((b) => b > rating) ?? (next ? next.minRating : null);
  const unlocks: Unlock[] = [];
  for (let i = 0; i <= idx; i++) {
    for (const u of RANK_TIERS[i]!.unlocks) if (i < idx || u.atSubRank === undefined || subRank >= u.atSubRank) unlocks.push(u.unlock);
  }
  return { tier: def.tier, subRank, rating, nextAt, unlocks };
}

export function hasUnlock(rating: number, u: Unlock): boolean {
  return tierFor(rating).unlocks.includes(u);
}

/** Fold a run into a dungeon's RatingRecord (pure). */
export function updateRecord(
  prev: RatingRecord | undefined,
  run: { dungeonId: DungeonId; level: number; timed: boolean; elapsedMs: Ms; timerMs: Ms; runIndex: number },
): RatingRecord {
  const rec: RatingRecord = prev ? structuredClone(prev) : { dungeonId: run.dungeonId, best: null, bestAttempt: null };
  const score = scoreRun(run);
  if (run.timed && (!rec.best || score > rec.best.score)) {
    rec.best = { level: run.level, score, elapsedMs: run.elapsedMs, timerMs: run.timerMs, runIndex: run.runIndex };
  }
  if (!rec.bestAttempt || run.level > rec.bestAttempt.level || (run.level === rec.bestAttempt.level && run.timed && !rec.bestAttempt.timed)) {
    rec.bestAttempt = { level: run.level, timed: run.timed, runIndex: run.runIndex };
  }
  return rec;
}
